import React from "react";
import { IoArrowForward } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { currencySymbols } from "../Quiezhome/Quizhome";
import { courseAmount, redeemedAmount } from "../../utils/recoil";
import { useRecoilValue } from "recoil";

const FullReportUnlockCard = ({ onUnlockFullResult }) => {
  const navigate = useNavigate();
  const _courseAmount = useRecoilValue(courseAmount);
  const amount = useRecoilValue(redeemedAmount);

  const handleUnlockFullResult = () => {
    if (onUnlockFullResult) {
      onUnlockFullResult();
      return;
    }

    navigate("/home");
  };

  return (
    <section className="relative isolate mt-8 overflow-hidden rounded-2xl bg-[#EEF7FF] px-10 py-12 shadow-[0_18px_45px_rgba(57,118,249,0.08)] md-max:px-6 sm-max:mt-5 sm-max:px-5 sm-max:py-8">
      <CornerGem position="top-right" />
      <CornerGem position="bottom-left" />

      <div className="relative z-10 grid grid-cols-[0.8fr_1.2fr] items-center gap-14 lg-small:gap-10 md-max:grid-cols-1 md-max:gap-8">
        <div className="flex justify-center md-max:hidden">
          <ReportIllustration />
        </div>

        <div className="max-w-[820px]">
          <span className="mb-3 inline-flex rounded-full bg-[#DDEEFF] px-4 py-1 text-sm font-bold uppercase text-primary-blue">
            Unlock now
          </span>

          <h2 className="text-[2rem] font-bold leading-tight text-[#293142] sm-max:text-2xl">
            Find Your True Self & Perfect Career
          </h2>

          <div className="mt-5 flex flex-col gap-6 text-[1.35rem] leading-[1.45] text-[#3B4658] lg-small:text-lg sm-max:text-base">
            <p>
              This report is designed to help you clearly understand your
              personality and discover the career path that truly fits you. It
              goes beyond guesswork—giving you real insights into who you are,
              how you think, and where you’ll succeed.
            </p>

            <p>
              The report highlights your preferred learning environment and
              effective study patterns—helping you learn smarter and perform at
              your best.
            </p>

            <p>
              Plus, chat with UVA – your AI Mentor for personalized guidance on
              courses, career fit, and universities, so you can confidently take
              the right next step toward your ideal future.
            </p>

            <p>
              For just ₹499, unlock a deeper understanding of yourself and gain
              the clarity to choose the right direction with confidence.
            </p>

            <p>
              <strong>Know yourself. Choose better. Grow faster.</strong>
            </p>
          </div>

          <p className="mt-9 text-[3.25rem] font-extrabold leading-none text-[#293142] sm-max:text-4xl">
            {currencySymbols[_courseAmount?.currency_code] ??
              _courseAmount?.currency_code}
            {amount}
          </p>

          <button
            type="button"
            onClick={handleUnlockFullResult}
            className="mt-8 inline-flex min-h-[64px] min-w-[320px] items-center justify-center gap-3 rounded-full bg-primary-blue px-10 text-xl font-bold text-white shadow-blue transition hover:-translate-y-0.5 hover:bg-[#2F64D9] focus:outline-none focus:ring-4 focus:ring-primary-blue/25 sm-max:min-w-0 sm-max:w-full sm-max:px-6 sm-max:text-lg"
          >
            Unlock full results
            <IoArrowForward aria-hidden="true" className="text-3xl" />
          </button>

          <p className="mt-4 text-base font-semibold text-[#7A8798] sm-max:text-sm">
            30-day money-back guarantee if you are not satisfied.
          </p>
        </div>
      </div>
    </section>
  );
};

const CornerGem = ({ position }) => {
  const isTopRight = position === "top-right";

  return (
    <div
      aria-hidden="true"
      className={`absolute h-48 w-48 opacity-35 ${
        isTopRight
          ? "-right-8 -top-8 rotate-12"
          : "-bottom-12 -left-10 -rotate-12"
      }`}
    >
      <div className="absolute left-10 top-8 h-24 w-24 rotate-45 bg-[#B8DAFF]" />
      <div className="absolute left-16 top-2 h-20 w-20 rotate-45 bg-[#D8ECFF]" />
      <div className="absolute left-24 top-[72px] h-16 w-16 rotate-45 bg-[#96C7FF]" />
      <div className="absolute left-0 top-28 h-10 w-10 rotate-[20deg] bg-[#CBE5FF]" />
    </div>
  );
};

const ReportIllustration = () => (
  <svg
    aria-hidden="true"
    className="h-auto w-full max-w-[430px]"
    viewBox="0 0 430 310"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <ellipse cx="205" cy="282" rx="172" ry="18" fill="#D7E5F1" />

    <g transform="translate(36 116)">
      <path d="M20 64H86V75H20V64Z" fill="#7E93A4" />
      <path d="M27 75H78L88 164H75L68 94H38L30 164H17L27 75Z" fill="#8B96A1" />
      <path d="M41 7H58V64H41V7Z" fill="#7D8D9B" />
      <path d="M33 14L48 10L60 56L45 60L33 14Z" fill="#5D6B77" />
      <path d="M12 80H94V96H12V80Z" fill="#3B92B6" />
      <path d="M29 69H77L86 80H20L29 69Z" fill="#69B4CF" />
      <path d="M45 96H62L54 130H37L45 96Z" fill="#BDE6F7" />
      <path d="M40 130H68L73 148H35L40 130Z" fill="#6CB6D3" />
      <circle cx="52" cy="139" r="4" fill="#EEF7FF" />
      <circle cx="62" cy="144" r="3" fill="#EEF7FF" />
    </g>

    <g transform="translate(176 52)">
      <path
        d="M101 0L93 60L181 66L184 245H159L149 84L41 78L31 241H8L22 56L87 60L92 0H101Z"
        fill="#7FB5E8"
      />
      <path d="M88 16L112 18L108 44L85 43L88 16Z" fill="#5E9AD9" />
      <path
        d="M42 66L179 75L171 212L32 203L42 66Z"
        fill="#F7FBFF"
        stroke="#B9CDE1"
        strokeWidth="2"
      />
      <path d="M42 66L179 75L177 88L40 80L42 66Z" fill="#75B7F2" />
      <path
        d="M78 113L115 96L146 120L135 160L94 169L67 139L78 113Z"
        fill="#438CD6"
      />
      <path d="M115 96L118 136L78 113L115 96Z" fill="#AAD7FF" />
      <path d="M118 136L146 120L135 160L118 136Z" fill="#2E73C2" />
      <path d="M118 136L94 169L67 139L118 136Z" fill="#68B3F1" />
      <path d="M78 113L118 136L67 139L78 113Z" fill="#1F66B3" />
      <path d="M118 136L115 96L146 120L118 136Z" fill="#7FC4FA" />
      <path d="M46 181L90 184" stroke="#B9CDE1" strokeWidth="3" />
      <path d="M46 193L103 197" stroke="#B9CDE1" strokeWidth="3" />
      <path d="M45 205L112 209" stroke="#B9CDE1" strokeWidth="3" />
      <path d="M130 177L141 178L136 211L125 210L130 177Z" fill="#8CC8F5" />
      <path d="M151 157L164 158L159 212L146 211L151 157Z" fill="#4E9FE0" />
    </g>

    <g transform="translate(117 73)">
      <path
        d="M50 62L87 35L122 58L119 105L82 129L47 106L50 62Z"
        fill="#4B5663"
      />
      <path d="M72 118L109 120L123 224L67 224L72 118Z" fill="#68AEEB" />
      <path d="M84 126L108 121L119 171L87 169L84 126Z" fill="#357BD0" />
      <path d="M71 132L87 169L67 224L48 224L71 132Z" fill="#A9D7FF" />
      <path
        d="M102 86C95 109 80 121 64 117C47 113 39 96 45 74C51 51 67 39 84 43C101 47 108 64 102 86Z"
        fill="#F1C8A6"
      />
      <path d="M56 45L103 71L111 48L72 25L56 45Z" fill="#38424D" />
      <path d="M49 72L65 55L58 93L45 91L49 72Z" fill="#38424D" />
      <circle cx="64" cy="79" r="4" fill="#293142" />
      <circle cx="89" cy="75" r="4" fill="#293142" />
      <path d="M74 93L84 90L83 99L74 93Z" fill="#D99877" />
      <path d="M70 105L99 98L86 114L70 105Z" fill="#F7FBFF" />
      <path d="M73 105L96 99L85 110L73 105Z" fill="#2F7BC8" />
      <path
        d="M53 116L42 146L17 135L22 121L40 128L47 111L53 116Z"
        fill="#F1C8A6"
      />
      <path d="M16 135L6 126L12 112L25 121L16 135Z" fill="#F1C8A6" />
      <path d="M111 119L137 146L127 157L105 136L111 119Z" fill="#F1C8A6" />
      <path d="M137 146L152 139L159 152L142 161L137 146Z" fill="#F1C8A6" />
      <path d="M67 224H85L80 238H67V224Z" fill="#374151" />
      <path d="M104 224H121L124 238H111L104 224Z" fill="#374151" />
    </g>
  </svg>
);

export default FullReportUnlockCard;
